/**
 * Pure arithmetic for the quantity-mode composition editor (MDOT-24700).
 *
 * Edit model: AUM is a FIXED budget. Every change preserves the identity
 * `AUM = Σ rowInvestedMarketValue + cashMarketValue` (I1) by adjusting the cash row,
 * which MAY go negative — submit is blocked while it is (I4, enforced by the consumer).
 * A typed quantity, market value or weight is sized on Cold War (`./position-sizing`); what is
 * computed here is what stays on the frontend: the cash remainder and the weights a change of the AUM
 * re-derives (`weight = marketValue / AUM × 100`, cash included, summing to 100 % — I3). A row's
 * market value is never computed here: it is Cold War's, or the backend's on load. The results are
 * STORED on the rows so columns render them without computing, and cash sums those stored values: it
 * is the remainder of exactly what the market-value column shows.
 *
 * All arithmetic goes through `$root/utils/monetary-math` — no bare float math.
 */
import { labAdd, labDiv, labMul, labSub, labSum } from "$root/utils/monetary-math";
import type { Instrument } from "../const";

export type QuantityEditResult = {
	rows: Instrument[];
	cashMarketValue: number;
};

/**
 * Where a row's invested value comes from — exactly one state per row:
 * - `byValue`: held by value (`heldByValue`) — a nested portfolio, an index, a live proxy. It has
 *   no price, so its stored `marketValue` is the whole of what it is worth, whatever any pricing
 *   answer says.
 * - `unpriced`: a share row the pricing engine has no price for at the market date (MDOT-25191).
 *   Its value there is unknown, so it is worth nothing and cash absorbs the gap.
 * - `shares`: a priced share row, worth its stored `marketValue` — Cold War's, or the backend's on
 *   load — and nothing while it stores none (priced, not sized yet).
 * - `awaitingPrice`: a share row not priced yet — its stored `marketValue`, if it has one.
 */
type RowValuation =
	| { state: "byValue"; marketValue: number }
	| { state: "unpriced" }
	| { state: "shares"; marketValue: number }
	| { state: "awaitingPrice"; marketValue: number };

function valuationOf(row: Instrument): RowValuation {
	if (row.heldByValue) {
		return { state: "byValue", marketValue: row.marketValue ?? 0 };
	}
	if (row.unpriced) {
		return { state: "unpriced" };
	}
	if (row.price != null) {
		return { state: "shares", marketValue: row.marketValue ?? 0 };
	}
	return { state: "awaitingPrice", marketValue: row.marketValue ?? 0 };
}

/** One row's contribution to the invested market value, by its {@link RowValuation} state. */
export function rowInvestedMarketValue(row: Instrument): number {
	const valuation = valuationOf(row);
	switch (valuation.state) {
		case "byValue":
		case "shares":
		case "awaitingPrice":
			return valuation.marketValue;
		case "unpriced":
			return 0;
	}
}

/** Σ {@link rowInvestedMarketValue} over all non-cash, non-deleted rows. */
export function computeInvestedMarketValue(rows: Iterable<Instrument>): number {
	return labSum(
		Array.from(rows).filter((row) => !row.isCash && !row.deleted),
		rowInvestedMarketValue,
	);
}

/** Derived cash market value: `AUM − Σ rowInvestedMarketValue` (I1). May be negative. */
export function computeCashMarketValue(rows: Iterable<Instrument>, aum: number): number {
	return labSub(aum, computeInvestedMarketValue(rows));
}

/** Derived weight: `marketValue / AUM × 100`. Zero when the AUM is not a positive budget. */
export function deriveWeight(marketValue: number, aum: number): number {
	if (aum <= 0) {
		return 0;
	}
	return labMul(labDiv(marketValue, aum), 100);
}

/**
 * Minimum AUM that brings a negative cash position back to ≥ 0: current AUM + the missing
 * cash, ceiled to whole cents so a pre-filled suggestion never leaves a sub-cent negative
 * residue behind.
 */
export function minimumAumToCoverCash(currentAum: number, missingCash: number): number {
	return labAdd(currentAum, labDiv(Math.ceil(labMul(missingCash, 100)), 100));
}

/** True while the composition overspends the fixed AUM — submit must be disabled (I4). */
export function hasNegativeCash(rows: Iterable<Instrument>, aum: number): boolean {
	return computeCashMarketValue(rows, aum) < 0;
}

/**
 * Re-derives the cash row from `rows` so I1 holds: cash takes the AUM remainder and its weight. Every
 * other row is passed through as it is.
 */
export function rebalanceCash(rows: Instrument[], aum: number): QuantityEditResult {
	const cashMarketValue = computeCashMarketValue(rows, aum);
	return {
		rows: rows.map((row) => {
			if (row.isCash) {
				return { ...row, marketValue: cashMarketValue, weight: deriveWeight(cashMarketValue, aum) };
			}
			return row;
		}),
		cashMarketValue,
	};
}

/**
 * Applies a change of the fixed AUM budget itself — the "raise the portfolio AUM" recovery
 * path out of negative cash (I4). Quantities and prices are untouched, so every non-cash
 * row's weight re-derives against the new budget (deleted rows too, so a later restore
 * stays consistent) and the cash row absorbs the whole difference (I1). An unpriced row
 * (MDOT-25191) is passed through whole: it has no value at the market date to re-derive from.
 *
 * These are the only row weights not given by Cold War. A change of AUM moves no price and no share,
 * so each weight is market value / AUM, the same division Cold War makes. The step is due to move to
 * Cold War too.
 */
export function applyAumEdit(params: { rows: Instrument[]; aum: number }): QuantityEditResult {
	const { rows, aum } = params;
	const cashMarketValue = computeCashMarketValue(rows, aum);
	return {
		rows: rows.map((row) => {
			if (row.isCash) {
				return { ...row, marketValue: cashMarketValue, weight: deriveWeight(cashMarketValue, aum) };
			}
			if (valuationOf(row).state === "unpriced") {
				return row;
			}
			const marketValue = rowInvestedMarketValue(row);
			return { ...row, marketValue, weight: deriveWeight(marketValue, aum) };
		}),
		cashMarketValue,
	};
}
